import { get, writable, type Readable } from 'svelte/store';

export type CesiumOverlayMessageSeverity = 'info' | 'success' | 'warning' | 'error';

export interface CesiumOverlayMessageAction {
	label: string;
	loadingLabel?: string;
	handler: () => void | Promise<void>;
}

export interface CesiumOverlayMessage {
	id: string;
	/**
	 * When set, pushing another message with the same key replaces the existing
	 * one in place — useful for "data is stale" warnings that should not stack
	 * across repeated saves.
	 */
	dedupeKey?: string;
	severity: CesiumOverlayMessageSeverity;
	title?: string;
	description: string;
	action?: CesiumOverlayMessageAction;
	dismissible?: boolean;
}

export type PushOverlayMessageInput = Omit<CesiumOverlayMessage, 'id'> & { id?: string };

const messages = writable<CesiumOverlayMessage[]>([]);

export const cesiumOverlayMessages: Readable<CesiumOverlayMessage[]> = {
	subscribe: messages.subscribe
};

function nextId(): string {
	return `overlay-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Adds (or replaces, when `dedupeKey` matches an existing entry) a message in
 * the overlay. Returns the message id so callers can dismiss it later.
 */
export function pushOverlayMessage(input: PushOverlayMessageInput): string {
	const id = input.id ?? nextId();
	const message: CesiumOverlayMessage = {
		dismissible: true,
		...input,
		id
	};
	messages.update((list) => {
		if (message.dedupeKey) {
			const existingIndex = list.findIndex((m) => m.dedupeKey === message.dedupeKey);
			if (existingIndex !== -1) {
				const next = list.slice();
				next[existingIndex] = { ...message, id: list[existingIndex].id };
				return next;
			}
		}
		return [...list, message];
	});
	return id;
}

export function dismissOverlayMessage(id: string): void {
	messages.update((list) => list.filter((m) => m.id !== id));
}

export function dismissOverlayMessageByKey(dedupeKey: string): void {
	messages.update((list) => list.filter((m) => m.dedupeKey !== dedupeKey));
}

export function clearOverlayMessages(): void {
	messages.set([]);
}

export function hasOverlayMessage(dedupeKey: string): boolean {
	return get(messages).some((m) => m.dedupeKey === dedupeKey);
}
